// (c) chaehongee. Confidential and proprietary.

import { useCallback } from 'react'
import { buildApiUrl } from '../../../../services/http'

export type DownloadUrlFn = (url: string) => Promise<void>

/** Build a same-origin fetch URL, proxying cross-origin images via the backend. */
export function fetchableUrl(url: string): string {
    try {
        const parsed = new URL(url, window.location.href)
        if (parsed.origin === window.location.origin) return url
        return buildApiUrl(`/system/proxy_image?url=${encodeURIComponent(url)}`)
    } catch {
        return url
    }
}

export function useDownloadUrl(): DownloadUrlFn {
    const downloadUrl = useCallback<DownloadUrlFn>(async (url: string) => {
        const resolvedUrl = fetchableUrl(url)
        try {
            const response = await fetch(resolvedUrl)
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`)
            }
            const blob = await response.blob()
            if (!blob.type.startsWith('image/')) {
                throw new Error(`Non-image content-type: ${blob.type}`)
            }
            const blobUrl = URL.createObjectURL(blob)
            const a = document.createElement('a')
            const urlPath = new URL(url, window.location.href).pathname
            const baseName = urlPath.split('/')?.pop() || 'image.png'
            a.href = blobUrl
            a.download = baseName
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(blobUrl)
        } catch (err) {
            console.warn('[Canvas] download failed, falling back to new tab:', err)
            try {
                window.open(url, '_blank', 'noopener,noreferrer')
            } catch { }
        }
    }, [])

    return downloadUrl
}

export default useDownloadUrl

